import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {debounce} from 'lodash';
import {callAction, setUpStore} from 'client/framework';
import SimpleDropdown from 'generic/SimpleDropdown/SimpleDropdown.react';
import SimpleDropdownStore from 'generic/SimpleDropdown/SimpleDropdown.store';
import SubjectSearchDropdown from 'client/Dennis/Content/Queue/QueueSearchPanel/SubjectSearchDropdown/SubjectSearchDropdown.react';
import TextInput from 'generic/Forms/TextInput/TextInput.react';
import Button from 'sg/Button/Button.react';
import {MandarkTable, Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import MandarkSearchDropdown from 'client/styleguide/Dropdowns/MandarkSearchDropdown/MandarkSearchDropdown.react';
import sessionStore from 'client/Session/SessionStore';
import {supplementModes} from 'client/Supplements/SupplementManager/SupplementManager.react';
import supplementManagerStore from 'client/Supplements/SupplementManager/SupplementManager.store';
import supplementManagerActions from 'client/Supplements/SupplementManager/SupplementManager.actions';
import {pushQueryParams} from 'client/history';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {AuthoringSubjectModelV1} from 'resources/GeneratedModels/AuthoringSubject/AuthoringSubjectModel.v1';
import supplementActions from 'client/Supplement/SupplementActions';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import './supplement-search.scss';

/* SupplementSearch

  * Upon mounting, SupplementSearch pings Mandark for the subjects belonging
  * to our selected domain. (The selected domain is 'Test Prep' by default.)
  * Once we've received the list of subjects, we use it to populate the subjects
  * dropdown and then render our filters and our table.

  * Each dropdown's selected value gets piped into the query that populates the table.
  * Any time the user fires the subject dropdown's onChange, we write the newly selected
  * subject to the search component store.  This serves as a 'control' variable against
  * which we determine if the filters should re-render with updated stores.
  * E.g., after selecting a new domain, we must ping Mandark for an updated list of
  * subjects that belong to the new domain.
  */

function getSubjectsQuery(subject) {
  if (sessionStore.isSuper()) {
    return resource('authoring_subjects_v1')
      .mandarkEndpoint(['authoring_subjects_v1'])
      .fields({
        authoring_subject_v1: 'domain,id,name'
      })
      .pageSize('infinite')
      .sort('domain,name');
  }
  return resource('authoring_subjects_v1')
    .mandarkEndpoint(['authors_v1', sessionStore.getUserId(), 'authoring_subjects_v1'])
    .pageSize('infinite')
    .filter(
      {
        id: subject
      },
      subject
    )
    .sort('domain,name');
}

function getSupplementsQuery({owner, questionSet, searchString = '', subject, type}) {
  return resource('authoring_supplements_v1')
    .mandarkEndpoint(['authoring_supplements_v1'])
    .pageSize(10)
    .include('authoring_subject_v1')
    .include('owner_v2', sessionStore.isSuper())
    .customQuery({
      with_meta: 'authoring_supplement_v1'
    })
    .filter(
      {
        authoring_subject_v1: {
          id: subject
        }
      },
      subject
    )
    .filter(
      {
        authoring_subject_v1: {
          authors_v1: sessionStore.getUserId()
        }
      },
      !sessionStore.isSuper()
    )
    .filter(
      {
        authoring_subject_v1: {
          authoring_question_sets_v1: questionSet
        }
      },
      !sessionStore.isSuper() && questionSet
    )
    .filter(
      {
        type
      },
      type
    )
    .filter(
      {
        owner_v2: {
          id: owner
        }
      },
      sessionStore.isSuper() && owner
    )
    .filter(
      {
        name: {
          case_insensitive_substring: encodeURIComponent(searchString)
        }
      },
      searchString
    ); // TODO: Add filter by owner once owner_v2 relationshipMeta is exposed
}

const subjectDropdownStore = setUpStore(
  SimpleDropdownStore,
  'SupplementSearchSubjectDropdownStore'
);
const typeDropdownStore = setUpStore(SimpleDropdownStore, 'SupplementSearchTypeDropdownStore');

export const SupplementSearch = awaitMandarkQueries(
  (props) => ({
    queries: {
      subjects: getSubjectsQuery(props.activeSubjectId),
      supplements: getSupplementsQuery({
        owner: props.owner,
        questionSet: props.questionSetId,
        subject: props.activeSubjectId,
        searchString: props.searchString,
        type: props.type
      })
    }
  }),
  class SupplementSearch extends React.Component {
    static propTypes = {
      activeSubjectId: PropTypes.string,
      owner: PropTypes.string,
      questionSetId: PropTypes.string,
      searchString: PropTypes.string,
      showInsertButton: PropTypes.bool,
      subjects: ImmutablePropTypes.list,
      type: PropTypes.string,
      typeOptions: ImmutablePropTypes.list
    };

    static defaultProps = {
      showInsertButton: false
    };

    constructor() {
      super();

      this.debouncedSetSearchString = debounce(this.setSearchString, 500);
    }

    getSupplementsQuery() {
      return getSupplementsQuery({
        owner: this.props.owner,
        questionSet: this.props.questionSetId,
        subject: this.props.activeSubjectId,
        searchString: this.props.searchString,
        type: this.props.type
      });
    }

    getSubjectOptions() {
      if (sessionStore.isSuper()) {
        const allOption = AuthoringSubjectModelV1.getDefaultModel()
          .setId('')
          .setName('All');
        return this.props.subjects.unshift(allOption);
      }
      return this.props.subjects;
    }

    setSearchString(string) {
      pushQueryParams({
        supplementSearchStr: string
      });
    }

    handleSearchInput = (e) => {
      this.debouncedSetSearchString(e.target.value);
    };

    handleSubjectSearchOptionChange(option) {
      pushQueryParams({supplementSelectedSubject: option ? option.get('id') : ''});
    }

    generateActionButtons(supplement) {
      const buttonGroup = [
        <Button
          className='supplement-search__action-btn supplement-search__action-btn--edit'
          color='gray'
          key='edit'
          onClick={() => {
            pushQueryParams({
              supplement: supplement.getId(),
              supplementMode: supplementModes.EDIT
            });
          }}
          small
          text='Edit'
        />
      ];

      if (this.props.showInsertButton) {
        buttonGroup.push(
          <Button
            className='supplement-search__action-btn supplement-search__action-btn--add'
            color='green'
            key='add'
            onClick={() => {
              callAction(supplementManagerActions.INSERT_SUPPLEMENT_AT_CURSOR_POSITION, {
                fieldPath: supplementManagerStore.getCurrentEditorField(),
                supplement
              });
              callAction(supplementManagerActions.SHOW_MODAL, false);
              callAction(supplementActions.ADD_SUPPLEMENT, supplement);
            }}
            small
            text='Add'
          />
        );
      }

      return buttonGroup;
    }

    getAuthorByIdQuery(id) {
      return resource('author_v1').mandarkEndpoint(['authors_v1', id]);
    }

    getAuthorsByEmailQuery(searchString) {
      return resource('author_v1')
        .mandarkEndpoint(['authors_v1'])
        .filter({
          email: {
            case_insensitive_substring: encodeURIComponent(searchString)
          }
        })
        .fields({
          authors_v1: 'id, email'
        });
    }

    getFilters() {
      return (
        <div className='supplement-search__filters'>
          <SubjectSearchDropdown
            className='supplement-search__filters-input'
            defaultSelectedId={this.props.activeSubjectId}
            handleOptionChange={this.handleSubjectSearchOptionChange}
            storeName='SupplementSubjectsSearchStore'
            queryParamName='supplementSelectedSubject'
          />
          <SimpleDropdown
            activeItemId={this.props.type}
            className='supplement-search__filters-input supplement-search__filters-input--type'
            fullWidth
            label='Type'
            options={this.props.typeOptions}
            onChange={(item) => pushQueryParams({supplementType: item.get('id')})}
            placeholder='Choose one'
            storeName={typeDropdownStore.getName()}
          />
          <MandarkSearchDropdown
            className='supplement-search__filters-input supplement-search__filters-input--owner'
            defaultSelectedId={this.props.owner}
            displayName='email'
            onClick={this.handleOwnerOptionChange}
            byIdQuery={this.getAuthorByIdQuery}
            indexQuery={this.getAuthorsByEmailQuery}
            label='Author Email'
            placeholder='Search by author email'
            renderFunc={(option) => (
              <div className='supplement-search__search-dropdown-option'>
                <div className='supplement-search__search-dropdown-option-email'>
                  {option.getEmail()}
                </div>
                <div className='supplement-search__search-dropdown-option-name'>
                  {option.getFullName()}
                </div>
              </div>
            )}
            storeName='SupplementSearchAuthorEmailSearch'
          />
          <TextInput
            className='supplement-search__filters-input supplement-search__filters-input--search'
            label='Supplement Name'
            maxLength={128}
            onChange={this.handleSearchInput}
            placeholder='Search by name'
            type='search'
            value={this.props.searchString}
          />
          <Button
            className='supplement-search__filters-button supplement-search__filters-button--new-supplement'
            text='New supplement'
            onClick={() => {
              pushQueryParams({
                supplement: null,
                supplementMode: supplementModes.EDIT
              });
            }}
          />
        </div>
      );
    }

    handleOwnerOptionChange(option) {
      pushQueryParams({
        supplementOwner: option ? option.getId() : null
      });
    }

    render() {
      return (
        <div className='supplement-search'>
          <div className='supplement-search__filters-wrapper filters-wrapper--supplements'>
            <h1 className='supplement-search__heading'>Filters</h1>
            {this.getFilters()}
          </div>
          <div className='supplement-search__results-wrapper'>
            <h1 className='supplement-search__heading'>Supplements</h1>
            <div className='supplement-search__results'>
              <MandarkTable
                className='supplement-search__results-table'
                query={this.getSupplementsQuery()}
                storeName='supplementMandarkTableStore'
              >
                <Column
                  heading='Type'
                  content={(supplement) =>
                    supplement
                      .getType()
                      .charAt(0)
                      .toUpperCase() +
                    supplement
                      .getType()
                      .slice(1)
                      .replace(/-/g, ' ')
                  }
                />
                <Column
                  heading='Name'
                  content={(supplement) => supplement.getName()}
                  sortBy='name'
                />
                <Column
                  heading='Owner'
                  content={(supplement) => supplement.getMeta().getOwnerEmail() || 'N/A'}
                />
                <Column
                  heading=''
                  content={(supplement) => this.generateActionButtons(supplement)}
                />
              </MandarkTable>
            </div>
          </div>
        </div>
      );
    }
  }
);
